import { Injectable, computed, effect, signal } from '@angular/core';
import { Subject } from 'rxjs';

export interface LayoutConfig {
  preset?: string;
  primary?: string;
  surface?: string | undefined | null;
  menuMode?: string;
}

interface LayoutState {
  staticMenuDesktopInactive?: boolean;
  overlayMenuActive?: boolean;
  configSidebarVisible?: boolean;
  staticMenuMobileActive?: boolean;
  menuHoverActive?: boolean;
}

interface MenuChangeEvent {
  key: string;
  routeEvent?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  _config: LayoutConfig = {
    preset: 'Aura',
    primary: 'sky',
    surface: 'soho',
    menuMode: 'static',
  };

  _state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
  };

  layoutConfig = signal<LayoutConfig>(this._config);

  layoutState = signal<LayoutState>(this._state);

  private configUpdate = new Subject<LayoutConfig>();

  private overlayOpen = new Subject<void>();

  private menuSource = new Subject<MenuChangeEvent>();

  private resetSource = new Subject<void>();

  menuSource$ = this.menuSource.asObservable();

  resetSource$ = this.resetSource.asObservable();

  configUpdate$ = this.configUpdate.asObservable();

  overlayOpen$ = this.overlayOpen.asObservable();

  isSidebarActive = computed(
    () => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive
  );

  getPrimary = computed(() => this.layoutConfig().primary);

  getSurface = computed(() => this.layoutConfig().surface);

  isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

  constructor() {
    effect(() => {
      const config = this.layoutConfig();
      if (config) {
        this.onConfigUpdate();
      }
    });
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      const overlayMenuActive = !this.layoutState().overlayMenuActive;

      this.layoutState.update(prev => ({ ...prev, overlayMenuActive }));

      if (overlayMenuActive) {
        this.overlayOpen.next();
      }

      return;
    }

    if (this.isDesktop()) {
      const staticMenuDesktopInactive = !this.layoutState().staticMenuDesktopInactive;

      this.layoutState.update(prev => ({ ...prev, staticMenuDesktopInactive }));
    } else {
      const staticMenuMobileActive = !this.layoutState().staticMenuMobileActive;

      this.layoutState.update(prev => ({ ...prev, staticMenuMobileActive }));

      if (staticMenuMobileActive) {
        this.overlayOpen.next();
      }
    }
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isMobile() {
    return !this.isDesktop();
  }

  onConfigUpdate() {
    this._config = { ...this.layoutConfig() };
    this.configUpdate.next(this.layoutConfig());
  }

  onMenuStateChange(event: MenuChangeEvent) {
    this.menuSource.next(event);
  }

  reset() {
    this.resetSource.next();
  }
}
